import React, { useEffect, useState } from 'react';
import styles from './Footer.module.css';
import cn from 'classnames';
import { ReactComponent as LogoFooter } from '../../assets/icons/LogoFooter.svg';
import { Link, useNavigate } from 'react-router-dom';
import {
  PARKING_PAGE,
  BUSINESS_PAGE,
  CONTACTS_PAGE,
  PROJECTS_PAGE,
  CAREERS_PAGE,
  SUPPORT_PAGE,
  MORTGAGE_PAGE,
  PROMO_PAGE,
  EXCURSIONS_PAGE,
  MANAGEMENT_PAGE,
  PRIVACY_PAGE,
  TERMS_PAGE,
  ABOUT_COMPANY_PAGE,
  NEWS_PAGE,
  MAIN_PAGE,
} from '../../router/paths';
import Icon from '../Icon';
import Button from '../UI/Button/Button';
import PhoneCallModal from '../PhoneCallModal/PhoneCallModal';

type FooterProps = {
  theme: 'light' | 'dark';
};

const Footer: React.FC<FooterProps> = ({ theme }) => {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isPhoneModalActive, setIsPhoneModalActive] = useState(false);

  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth);
    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, []);

  const wrapperClass = cn(styles.wrapper, {
    [styles.light]: theme === 'light',
    [styles.dark]: theme === 'dark',
  });

  const handleNewsClick = () => {
    navigate(NEWS_PAGE);
  };

  const handleSupportClick = () => {
    navigate(SUPPORT_PAGE);
  };

  const handlePhoneClick = () => {
    setIsPhoneModalActive(true);
  };

  const getButtonColorType = (isDarkTheme: boolean) => {
    return isDarkTheme ? 'dark_gray' : 'gray';
  };

  const buttonStyle = {
    width: windowWidth <= 710 ? '100%' : 'fit-content',
  };

  return (
    <div className={wrapperClass}>
      <PhoneCallModal 
        isActive={isPhoneModalActive} 
        onClose={() => setIsPhoneModalActive(false)} 
      />
      <div className={styles.inner_top}>
        <div className={styles.top_element}>
          <p className='text_subheaders_sh1'>Рассказываем о&nbsp;достижениях</p>
          <Button
            roundType='small_rounded'
            colorType={getButtonColorType(theme === 'dark')}
            onClick={handleNewsClick}
            style={buttonStyle}
          >
            Смотреть новости
          </Button>
        </div>
        <div className={styles.top_element}>
          <p className='text_subheaders_sh1'>+7 938 151-22-22</p>
          <Button
            roundType='small_rounded'
            colorType={theme === 'dark' ? 'gray' : 'wb'}
            style={buttonStyle}
            onClick={handlePhoneClick}
          >
            Позвонить
          </Button>
        </div>
        <div className={styles.top_element}>
          <p className='text_subheaders_sh1'>Есть вопросы?</p>
          <Button
            roundType='small_rounded'
            colorType={getButtonColorType(theme === 'dark')}
            onClick={handleSupportClick}
            style={buttonStyle}
          >
            Смотреть ответы
          </Button>
        </div>
      </div>
      <div className={styles.inner_bottom}>
        <div className={styles.left}>
          <div className={styles.top}>
            <Link to={MAIN_PAGE}>
              <LogoFooter width='70' height='67' fill='currentColor'></LogoFooter>
            </Link>
          </div>
          <div className={cn('text_body_b3', styles.middle)}>
            <Link to={TERMS_PAGE}>
              Пользовательское соглашение
            </Link>
            <Link to={PRIVACY_PAGE}>
              Политика обработки персональных данных
            </Link>
          </div>
          <div className={cn('text_captions_c2', styles.bottom)}>
            <p>{new Date().getFullYear()}</p>
            <p>
              ООО СЗ «
              <a
                href={ABOUT_COMPANY_PAGE}
                style={{ cursor: 'pointer' }}
              >
                ВОСТОК-СТРОЙ
              </a>
              »
            </p>
            <p>
              СОЗДАНО В{' '}
              <a
                href="https://impulse-group.org/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer' }}
              >
                IMPULSE
              </a>
            </p>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.li}>
            <div className={styles.li_header}>
              <p className={cn('text_captions_c2', styles.li_header_text)}>Недвижимость</p>
            </div>
            <div className={cn('text_body_b3', styles.li_content)}>
              <Link to={PROJECTS_PAGE}>
                Проекты
              </Link>
              <Link to={BUSINESS_PAGE}>
                Коммерция
              </Link>
              <Link to={PARKING_PAGE}>
                Машино-места
              </Link>
            </div>
          </div>
          <div className={styles.li}>
            <div className={styles.li_header}>
              <p className={cn('text_captions_c2', styles.li_header_text)}>Сервисы</p>
            </div>
            <div className={cn('text_body_b3', styles.li_content)}>
              <Link to={MORTGAGE_PAGE}>
                Рассрочка
              </Link>
              <Link to={PROMO_PAGE}>
                Акции и скидки
              </Link>
              <Link to={EXCURSIONS_PAGE}>
                Экскурсии
              </Link>
            </div>
          </div>
          <div className={styles.li}>
            <div className={styles.li_header}>
              <p className={cn('text_captions_c2', styles.li_header_text)}>Компания</p>
            </div>
            <div className={cn('text_body_b3', styles.li_content)}>
              <Link to={ABOUT_COMPANY_PAGE}>
                О компании
              </Link>
              <Link to={NEWS_PAGE}>
                Новости
              </Link>
              <Link to={SUPPORT_PAGE}>
                Поддержка
              </Link>
              <Link to={CONTACTS_PAGE}>
                Контакты
              </Link>
              <Link to={CAREERS_PAGE}>
                Карьера
              </Link>
              <Link to={MANAGEMENT_PAGE}>
                Домоуправление
              </Link>
            </div>
          </div>
          <div className={styles.li}>
            <div className={styles.li_header}>
              <p className={cn('text_captions_c2', styles.li_header_text)}>Соцсети</p>
            </div>
            <div className={cn('text_body_b3', styles.li_content)}>
              <div className={styles.li_external}>
                <Link to={'https://www.youtube.com/@vostok_stroy'}>Ютуб</Link>
                <Icon name='externalLinkIcon' width='8' height='8'></Icon>
              </div>
              <div className={styles.li_external}>
                <Link to={'https://vk.com/vostok_stroy_kaspiysk'}>Вконтакте</Link>
                <Icon name='externalLinkIcon' width='8' height='8'></Icon>
              </div>
              <div className={styles.li_external}>
                <Link to={'https://t.me/vostok_stroy'}>Телеграм</Link>
                <Icon name='externalLinkIcon' width='8' height='8'></Icon>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;